import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { hideMessage, showMessage } from '~/actions';
import { Box, Text } from '~/commonComponents';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { useNavigationDetector, useToggle } from '~/hooks';
import {
  IJobPostDetailEntity,
  JobPostingStatus,
  useSaveJobPostDetail,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { useJobDetailsForm } from '../../JobDetailsForm/hooks/useJobDetailsForm';
import { descriptionSchema, generalSchema, scheduleSchema } from '../../JobDetailsForm/schema';
import { JobDetailsFormMapper } from '../../JobDetailsForm/mappers/JobDetailsFormMapper';
import {
  GeneralJobInfoEditable,
  JobDescriptionEditable,
  ScheduleEditable,
} from '../../JobDetailsForm';

interface IJobDetailsTabViewProps {
  jobPosting?: IJobPostDetailEntity;
}

enum EditableSection {
  GeneralInfo = 'generalInfo',
  ScheduleInfo = 'scheduleInfo',
  JobDescription = 'jobDescription',
}

const sectionSchemas = {
  [EditableSection.GeneralInfo]: generalSchema,
  [EditableSection.ScheduleInfo]: scheduleSchema,
  [EditableSection.JobDescription]: descriptionSchema,
} as const;

export const JobDetailsTabView = ({ jobPosting }: IJobDetailsTabViewProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [editSection, setEditSection] = useState<EditableSection | null>(null);
  const [confirmationExitModalOpen, toggleConfirmationExitModal] = useToggle(false);

  const { mutate: saveJobPostDetail, isPending } = useSaveJobPostDetail();

  // Only allow editing when job posting status is active
  const isEditable = useMemo(
    () => jobPosting?.status === JobPostingStatus.Active,
    [jobPosting?.status],
  );

  const {
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { isValid, isDirty },
  } = useJobDetailsForm({
    initialValues: jobPosting,
    schema: editSection ? sectionSchemas[editSection] : undefined,
  });

  // Navigation blocking logic - block when there are unsaved changes
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: isDirty && editSection !== null, // Only block when editing and there are changes
    onNavigationAttempt: () => {
      // Show confirmation modal for any navigation attempt with unsaved changes
      toggleConfirmationExitModal.setOn();
    },
  });

  const handleCancelEdit = useCallback(() => {
    reset();
    setEditSection(null);
  }, [reset]);

  const handleSaveEdit = handleSubmit((data) => {
    if (!jobPosting?.id) return;

    const payload = JobDetailsFormMapper.toSaveJobPostDetailPayload(data);

    saveJobPostDetail(
      { id: jobPosting.id, payload },
      {
        onSuccess: () => {
          // Invalidate query to refetch latest data in parent component
          queryClient.invalidateQueries({
            queryKey: ['GET_JOB_POSTING_DETAIL', jobPosting.id],
          });

          setEditSection(null);
          setTimeout(() => {
            dispatch(
              showMessage({
                popupProps: {
                  rightButtonText: 'Close',
                  rightButtonAction: () => dispatch(hideMessage()),
                },
                title: 'Changes Saved!',
                message: 'Your updates have been saved successfully.',
                isSuccess: true,
              }),
            );
          }, 500);
        },
        onError: (error) => {
          showErrorMessage({ error });
        },
      },
    );
  });

  return (
    <Box gap={32} width={892} marginTop={8} marginInline="auto">
      {/* Temporarily hidden as it’s not included in the MVP */}
      {/* <Row
        alignSelf="center"
        alignItems="center"
        border={`1.5px solid ${Colors.neutral_100}`}
        borderRadius={6}
        paddingInline={65}
        paddingBlock={12}
        gap={42}
      >
        <Row gap={6}>
          <Text fontSize={18}>Views:</Text>
          <Text fontSize={20} bold>
            1672
          </Text>
        </Row>
        <Divider width={2} height={24} marginVertical={0} />
        <Row gap={6}>
          <Text fontSize={18}>Applied:</Text>
          <Text fontSize={20} bold>
            77
          </Text>
        </Row>
        <Divider width={2} height={24} marginVertical={0} />
        <Row gap={6}>
          <Text fontSize={18}>Interviewed:</Text>
          <Text fontSize={20} bold>
            10
          </Text>
        </Row>
      </Row> */}

      <Box gap={14}>
        <Text fontSize={22} bold>
          Job Details
        </Text>

        {/* General Job Information Section */}
        <GeneralJobInfoEditable
          readonlyFields={['office', 'professionType']}
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.GeneralInfo}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.GeneralInfo)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.GeneralInfo)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />

        {/* Schedule Section */}
        <ScheduleEditable
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.ScheduleInfo}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.ScheduleInfo)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.ScheduleInfo)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />

        {/* Job Description Section */}
        <JobDescriptionEditable
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.JobDescription}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.JobDescription)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.JobDescription)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />
      </Box>

      <LeaveInterviewCreationPopup
        isOpen={confirmationExitModalOpen}
        onDiscard={() => {
          reset();
          setEditSection(null);
          toggleConfirmationExitModal.setOff();
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          clearPendingNavigation();
          toggleConfirmationExitModal.setOff();
        }}
      />
    </Box>
  );
};
